import './conversation-summary-modal.scss';

import { isEmpty, orderBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useLastMindMessage } from '../../../../data/data/conversations/use-last-mind-message';
import { ChatMessagePostedByType } from '../../../../data/data/enums';
import { ChatMessageItem } from '../../../../data/data/types';
import {
  ConversationItemDataFragment,
  ConversationSummaryMessageInput,
  UserType,
} from '../../../../data/types/api/graphql';
import { Button, ButtonType } from '../../../../elements/button/button';
import {
  ChatPatient,
  ChatPatientType,
} from '../../../../elements/chat/chat-patient';
import { FormLabel, FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';
import { CopyTextArea } from '../../../../elements/utils/copy-text-area/copy-text-area';
import { CopyTextAreaWithFilter } from '../../../../elements/utils/copy-text-area/copy-text-area-with-filter';

export type ChatMessageSummaryItem = {
  id: string;
  messageItem: ChatMessageItem;
  text: string | null | undefined;
};

type ConversationSummaryModalProps = {
  conversationItem: ConversationItemDataFragment;
  selectedChatMessageItems: ChatMessageSummaryItem[];
  onCancel?: () => void;
  onClose?: () => void;
};

export function ConversationSummaryModal({
  conversationItem,
  selectedChatMessageItems,
  onCancel,
  onClose,
}: ConversationSummaryModalProps) {
  const postedByToUserType = useCallback(
    (type: ChatMessagePostedByType): UserType => {
      switch (type) {
        case ChatMessagePostedByType.PATIENT:
          return UserType.Patient;
        case ChatMessagePostedByType.SYSTEM:
          return UserType.System;
        default:
          return UserType.HealthProfessional;
      }
    },
    []
  );

  const getSelectedMessages = useCallback(
    (types: ChatMessagePostedByType[]) => {
      const messages = orderBy(
        selectedChatMessageItems,
        option => option.messageItem.postedAt,
        ['asc']
      )
        .filter(
          option =>
            types.includes(option.messageItem.postedBy.type) &&
            !isEmpty(option.text)
        )
        .map(option => option.text?.replace(/(\r\n|\n|\r)+/gm, '\n'));

      return messages.join('\n\n');
    },
    [selectedChatMessageItems]
  );

  const getSelectedMessageInputs = useCallback(
    (types: ChatMessagePostedByType[]): ConversationSummaryMessageInput[] =>
      orderBy(selectedChatMessageItems, option => option.messageItem.postedAt, [
        'asc',
      ])
        .filter(
          option =>
            types.includes(option.messageItem.postedBy.type) &&
            !isEmpty(option.text)
        )
        .map(option => ({
          sender: postedByToUserType(option.messageItem.postedBy.type),
          text: option.text!.replace(/(\r\n|\n|\r)+/gm, '\n'),
        })),
    [selectedChatMessageItems, postedByToUserType]
  );

  const {
    patientMessages,
    healthProfessionalMessages,
    patientMessageInputs,
    healthProfessionalMessageInputs,
  } = useMemo(
    () => ({
      patientMessages: getSelectedMessages([ChatMessagePostedByType.PATIENT]),
      healthProfessionalMessages: getSelectedMessages([
        ChatMessagePostedByType.HEALTH_PROFESSIONAL,
        ChatMessagePostedByType.SYSTEM,
      ]),
      patientMessageInputs: getSelectedMessageInputs([
        ChatMessagePostedByType.PATIENT,
      ]),
      healthProfessionalMessageInputs: getSelectedMessageInputs([
        ChatMessagePostedByType.HEALTH_PROFESSIONAL,
        ChatMessagePostedByType.SYSTEM,
      ]),
    }),
    [getSelectedMessages, getSelectedMessageInputs]
  );

  const { data: lastMindMessage, loading: lastMindMessageLoading } =
    useLastMindMessage(conversationItem.id);

  const onHandleCancel = useCallback(() => {
    onCancel?.();

    onClose?.();
  }, [onCancel, onClose]);

  return (
    <Modal
      title={
        <CopyText text={conversationItem.title ?? ''}>
          {conversationItem.title ?? ''}
        </CopyText>
      }
      width={ModalWidth.LARGE}
      onClose={onClose}
      buttons={
        <Button type={ButtonType.SECONDARY} inverted onClick={onHandleCancel}>
          <CustomFormattedMessage id="common.cancel" />
        </Button>
      }
    >
      <ChatPatient
        type={ChatPatientType.EXTENDED}
        patient={conversationItem.patient}
      />

      <FormRow>
        <FormLabel>
          <CustomFormattedMessage id="common.s-rule" />
        </FormLabel>
        {lastMindMessageLoading && (
          <CustomFormattedMessage id="common.loading" />
        )}
        {!lastMindMessageLoading && lastMindMessage?.sRule?.triageSummary && (
          <CopyTextArea text={lastMindMessage.sRule.triageSummary} />
        )}
      </FormRow>
      <FormRow>
        <FormLabel>
          <CustomFormattedMessage id="common.patient" />
        </FormLabel>
        <CopyTextAreaWithFilter
          text={patientMessages}
          messages={patientMessageInputs}
        />
      </FormRow>
      <FormRow>
        <FormLabel>
          <CustomFormattedMessage id="common.health-professional" />
        </FormLabel>
        <CopyTextAreaWithFilter
          text={healthProfessionalMessages}
          messages={healthProfessionalMessageInputs}
        />
      </FormRow>
    </Modal>
  );
}
